import { useEffect, useRef, useState } from "react";
import { Heart, CalendarDays, Sparkles, X } from "lucide-react";

const BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
const X_API_KEY = process.env.REACT_APP_X_API_KEY || "";

const presets = [
  { label: "$10", value: 10 },
  { label: "$25", value: 25 },
  { label: "$50", value: 50 },
  { label: "$100", value: 100 },
];

const intervals = [
  { label: "One-time", value: "one-time" },
  { label: "Every 3 days", value: "3days" },
  { label: "Every week", value: "week" },
  { label: "Every month", value: "month" },
  { label: "Every year", value: "year" },
];

export default function DonateButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState(25);
  const [frequency, setFrequency] = useState("month");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("one-time");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const handledReturnRef = useRef(false);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [thankYouText, setThankYouText] = useState("");
  const thankYouTimeoutRef = useRef(null);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const createOneTimeOrder = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/paypal/create-donation-order/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": X_API_KEY,
        },
        body: JSON.stringify({
          amount,
          currency: "USD",
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.id) {
        throw new Error(data?.msg || data?.detail || "Unable to start donation payment");
      }

      return {
        orderId: data.id,
        approvalUrl:
          data?.links?.find(
            (link) => link?.rel === "approve" || link?.rel === "payer-action"
          )?.href || null,
      };
    } catch (err) {
      console.error("Donation order error:", err);
      alert(err?.message || "Donation payment failed");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createSubscriptionPlan = async () => {
    setLoading(true);
    try {
      let interval = "month";
      let frequencyValue = 1;

      if (frequency === "3days") {
        interval = "day";
        frequencyValue = 3;
      } else if (frequency === "week") {
        interval = "week";
        frequencyValue = 1;
      } else if (frequency === "month") {
        interval = "month";
        frequencyValue = 1;
      } else if (frequency === "year") {
        interval = "year";
        frequencyValue = 1;
      }

      const response = await fetch(`${BASE_URL}/paypal/create-donation-subscription-plan/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": X_API_KEY,
        },
        body: JSON.stringify({
          amount,
          currency: "USD",
          interval,
          frequency: frequencyValue,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.plan_id) {
        throw new Error(data?.msg || "Unable to create donation subscription");
      }

      return {
        planId: data.plan_id,
        approvalUrl: data?.approval_url || null,
        subscriptionId: data?.subscription_id || null,
      };
    } catch (err) {
      console.error(err);
      alert(err?.message || "Subscription setup failed");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const captureDonation = async (orderId) => {
    try {
      const response = await fetch(`${BASE_URL}/paypal/capture-donation-order/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": X_API_KEY,
        },
        body: JSON.stringify({
          orderID: orderId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || "Donation capture failed");
      }

      closeModal();
      setThankYouText("Thank you for your donation.");
      setThankYouOpen(true);
      // auto-redirect after a short delay
      try {
        thankYouTimeoutRef.current = setTimeout(() => {
          try {
            window.location.assign("/");
          } catch (e) {
            window.location.href = "/";
          }
        }, 3000);
      } catch (e) {
        try {
          window.location.assign("/");
        } catch (err) {
          window.location.href = "/";
        }
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Donation capture failed");
    }
  };

  const confirmSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(`${BASE_URL}/paypal/confirm-donation-subscription/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": X_API_KEY,
        },
        body: JSON.stringify({
          subscription_id: subscriptionId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || "Donation confirmation failed");
      }

      closeModal();
      setThankYouText("Thank you for your support.");
      setThankYouOpen(true);
      try {
        thankYouTimeoutRef.current = setTimeout(() => {
          try {
            window.location.assign("/");
          } catch (e) {
            window.location.href = "/";
          }
        }, 3000);
      } catch (e) {
        try {
          window.location.assign("/");
        } catch (err) {
          window.location.href = "/";
        }
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Donation confirmation failed");
    }
  };

  const handleDonationTypeChange = (value) => {
    setMode(value);

    if (value === "one-time") {
      setFrequency("month");
    }
  };

  const startOneTimeDonation = async () => {
    setLoading(true);
    try {
      const data = await createOneTimeOrder();
      if (!data?.approvalUrl) {
        throw new Error("PayPal approval link could not be created");
      }

      localStorage.setItem("donation_type", "one-time");
      localStorage.setItem("donation_order_id", data.orderId);
      localStorage.setItem("donation_name", donorName);
      localStorage.setItem("donation_email", donorEmail);

      window.location.assign(data.approvalUrl);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Unable to start donation payment");
    } finally {
      setLoading(false);
    }
  };

  const startRecurringDonation = async () => {
    setLoading(true);
    try {
      const planData = await createSubscriptionPlan();

      if (!planData?.approvalUrl) {
        throw new Error("PayPal subscription approval link could not be created");
      }

      localStorage.setItem("donation_type", "recurring");
      localStorage.setItem("donation_plan_id", planData.planId || "");
      localStorage.setItem("donation_subscription_id", planData.subscriptionId || "");
      localStorage.setItem("donation_name", donorName);
      localStorage.setItem("donation_email", donorEmail);

      window.location.assign(planData.approvalUrl);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Unable to start recurring donation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (handledReturnRef.current || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const returnedToken =
      params.get("token") ||
      params.get("subscription_id") ||
      params.get("orderID") ||
      params.get("orderId");

    if (!returnedToken) return;

    handledReturnRef.current = true;

    const donationType = localStorage.getItem("donation_type");
    const savedOrderId = localStorage.getItem("donation_order_id");
    const savedSubscriptionId = localStorage.getItem("donation_subscription_id");

    if (donationType === "one-time") {
      captureDonation(savedOrderId || returnedToken);
    } else if (donationType === "recurring") {
      confirmSubscription(
        params.get("subscription_id") || savedSubscriptionId || returnedToken
      );
    }

    localStorage.removeItem("donation_type");
    localStorage.removeItem("donation_order_id");
    localStorage.removeItem("donation_plan_id");
    localStorage.removeItem("donation_subscription_id");
    localStorage.removeItem("donation_name");
    localStorage.removeItem("donation_email");

    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    return () => {
      if (thankYouTimeoutRef.current) {
        clearTimeout(thankYouTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <button
        onClick={openModal}
        className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100"
      >
        <Heart size={16} /> Donate Us
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 py-6">
          <div className="w-full max-w-2xl rounded-3xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Support AxionNest</h2>
                <p className="text-sm text-gray-500">
                  Choose an amount and payment plan that suits you.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-6 p-6 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="rounded-2xl bg-slate-50 p-5">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Sparkles size={16} className="text-amber-500" />
                  Donation details
                </div>

                <div className="mb-4 flex gap-2">
                  {[
                    { label: "One-time", value: "one-time" },
                    { label: "Recurring", value: "recurring" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleDonationTypeChange(option.value)}
                      className={`rounded-full px-3 py-2 text-sm font-medium transition ${
                        mode === option.value
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Amount (USD)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  />
                </div>

                <div className="mb-4 grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Your name
                    </label>
                    <input
                      type="text"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="Jane Doe"
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-2 gap-2">
                  {presets.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setAmount(preset.value)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                        amount === preset.value
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {mode === "recurring" && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Billing interval
                    </label>
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      {intervals.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-between rounded-2xl border border-gray-200 p-5">
                <div>
                  <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <CalendarDays size={16} className="text-emerald-500" />
                    Secure checkout
                  </div>
                  <p className="text-sm text-gray-600">
                    Your payment is processed securely by PayPal. You can support
                    AxionNest once or set up a recurring support plan.
                  </p>
                </div>

                <div className="mt-4">
                  {loading ? (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      Preparing payment…
                    </div>
                  ) : mode === "one-time" ? (
                    <button
                      onClick={startOneTimeDonation}
                      className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
                    >
                      Continue to PayPal
                    </button>
                  ) : (
                    <button
                      onClick={startRecurringDonation}
                      className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                    >
                      Continue to PayPal
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {thankYouOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 py-6">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-2xl">
            <div className="mb-3 flex items-center justify-center">
              <Sparkles size={28} className="text-amber-500" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Thank you</h3>
            <p className="mb-4 text-sm text-gray-600">{thankYouText}</p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setThankYouOpen(false);
                  if (thankYouTimeoutRef.current) clearTimeout(thankYouTimeoutRef.current);
                  try {
                    window.location.assign("/");
                  } catch (e) {
                    window.location.href = "/";
                  }
                }}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Return to home
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}